import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useStudioWorkspace } from "../../firestoreStudio/context/StudioWorkspaceProvider";
import { useRuntimeConnection } from "../../firestoreStudio/runtime/StudioRuntimeProvider";
import { useAllStudioConnections } from "../../firestoreStudio/hooks/useStudioConnections";
import { useFirestoreAdmin } from "../context/FirestoreAdminProvider";
import ConnectionSwitchDialog from "./ConnectionSwitchDialog";
import ConnectionVerifyDialog from "../../firestoreStudio/components/ConnectionVerifyDialog";
import ConnectionDeepTestDialog from "../../firestoreStudio/components/ConnectionDeepTestDialog";

export default function ExplorerPageHeader() {
  const { workspace } = useStudioWorkspace();
  const { projectId, app, firestore, runtimeVersion, isActive, reconnect } = useRuntimeConnection();
  const {
    registeredCollections,
    rows,
    lastDataRefresh,
    registrySyncing,
    runtimeReady,
    runtimeMismatch,
    rediscoverCollections,
    loadDocuments,
    loading,
  } = useFirestoreAdmin();
  const { data: connections = [] } = useAllStudioConnections();

  const [switchOpen, setSwitchOpen] = useState(false);
  const [healthOpen, setHealthOpen] = useState(false);
  const [deepTestOpen, setDeepTestOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const activeConn = connections.find((c) => c.id === workspace.connectionId);
  const runtimeLabel = app?.name || firestore?.app?.name || "—";

  const onRediscover = async () => {
    setBusy(true);
    try {
      await rediscoverCollections();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <header className="fa-explorer-header fa-card">
        <div className="fa-explorer-header-top">
          <div>
            <p className="fa-workspace-kicker">Firestore Manager</p>
            <h1 className="fa-explorer-title">Collection Explorer</h1>
          </div>
          <div className="fa-explorer-actions">
            <button type="button" className="fa-btn" onClick={() => loadDocuments()} disabled={loading || !runtimeReady}>
              Refresh
            </button>
            <button type="button" className="fa-btn" onClick={onRediscover} disabled={!runtimeReady || registrySyncing || busy}>
              {registrySyncing || busy ? "Rediscovering…" : "Rediscover"}
            </button>
            <button type="button" className="fa-btn" onClick={() => setSwitchOpen(true)}>
              Switch Workspace
            </button>
            <button type="button" className="fa-btn" onClick={() => setHealthOpen(true)} disabled={!activeConn}>
              Health Check
            </button>
            <button type="button" className="fa-btn" onClick={() => setDeepTestOpen(true)} disabled={!activeConn}>
              Deep Test
            </button>
            <button type="button" className="fa-btn" onClick={reconnect} disabled={!workspace.connectionId}>
              Reload Runtime
            </button>
            <Link to="/admin/firestore-studio" className="fa-btn">
              Firestore Studio
            </Link>
          </div>
        </div>

        <div className="fa-explorer-meta">
          <div><span>Workspace</span><strong>{workspace.projectName || "—"} / {workspace.connectionName || "—"}</strong></div>
          <div><span>Current Project</span><strong className="mono">{projectId || activeConn?.firebaseProjectId || "—"}</strong></div>
          <div><span>Current Runtime</span><strong className="mono">{runtimeLabel} · v{runtimeVersion}</strong></div>
          <div><span>Collections</span><strong>{registeredCollections.length}</strong></div>
          <div><span>Documents Loaded</span><strong>{rows.length.toLocaleString()}</strong></div>
          <div><span>Last Refresh</span><strong className="mono">{lastDataRefresh ? new Date(lastDataRefresh).toLocaleString() : "—"}</strong></div>
        </div>

        {!workspace.connectionId && (
          <div className="fa-workspace-alert">No active workspace connection.</div>
        )}
        {workspace.connectionId && !isActive && (
          <div className="fa-workspace-alert">Waiting for workspace runtime…</div>
        )}
        {runtimeMismatch && (
          <div className="fa-workspace-alert">
            Runtime mismatch detected.
            <button type="button" className="fa-btn fa-btn-primary ml-2" onClick={reconnect}>Reload Runtime</button>
          </div>
        )}
      </header>

      <ConnectionSwitchDialog open={switchOpen} connections={connections} onClose={() => setSwitchOpen(false)} />

      {activeConn && (
        <>
          <ConnectionVerifyDialog open={healthOpen} connection={activeConn} onClose={() => setHealthOpen(false)} />
          <ConnectionDeepTestDialog open={deepTestOpen} connection={activeConn} onClose={() => setDeepTestOpen(false)} />
        </>
      )}
    </>
  );
}
